<template>
  <div
    :class="['popover-menu__item', { 'popover-menu__item--disabled': isDisabled }, { highlighted }, { destructive }]"
  >
    <component
      :is="componentData.name"
      :data-test-id="`popover-menu-item-${computedId}`"
      :class="['popover-menu__item-component', { shortcut }]"
      ref="Component"
      v-bind="componentData.props"
      data-popover-menu-item
      v-close-popper="
        closeOnClick ||
        (componentData.name === 'a' && componentData.props.target !== '_blank') ||
        componentData.name === 'RouterLink'
      "
      @change="($event) => $emit('update:checked', $event.target.checked)"
    >
      <div v-if="hasSlot['info-icon']" class="info-icon">
        <slot name="info-icon" />
      </div>

      <div class="primary-text">
        <span :title="primaryText">{{ primaryText }}</span>
      </div>

      <ShortcutInfo v-if="shortcut" :shortcut="shortcut" class="popover-menu__item-shortcut" />

      <div v-if="!toggle && hasSlot['action-icon']" class="popover-menu__item-action-icon">
        <slot name="action-icon"></slot>
      </div>
    </component>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { kebabCase } from 'lodash-es';
import { hasSlotContent } from '@components/slots';

export default defineComponent({
  name: 'PopoverMenuItem',
  emits: { 'update:checked': null },

  props: {
    id: {
      type: String,
      default: '',
    },

    primaryText: {
      type: String,
      required: true,
    },

    shortcut: {
      type: String,
      default: '',
    },

    highlighted: {
      type: Boolean,
      default: false,
    },

    to: {
      type: Object as PropType<{ name: string; params?: Record<string, string>; query?: Record<string, string> }>,
      default: null,
    },

    href: {
      type: String,
      default: null,
    },

    target: {
      type: String,
      default: null,
    },

    /**
     * If true, this item will be marked as (different styling) a "destructive" item,
     * which means it performs a "delete" operation (or similar) on the data
     */
    destructive: {
      type: Boolean,
      default: false,
    },

    toggle: {
      type: Boolean,
      default: false,
    },

    isSelected: {
      type: Boolean,
      default: false,
    },

    /**
     * Only button and CheckboxToggle can be disabled
     */
    isDisabled: {
      type: Boolean,
      default: false,
    },

    /**
     * Close the popover menu when clicking / selecting the menu item
     */
    closeOnClick: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    computedId(): string {
      return this.id === '' ? kebabCase(this.primaryText) : this.id;
    },

    /**
     * Set component name and properties based on input props.
     *
     * Don't bind all input props all the time because router-link and anchor href
     * don't work together
     */
    componentData():
      | {
          name: 'a';
          props: {
            href: string;
            target: string;
          };
        }
      | { name: 'RouterLink'; props: { to: { name: string }; target: string } }
      | {
          name: 'CheckBoxToggle';
          props: {
            checked: boolean;
            position: 'end';
            id: string;
            disabled: boolean;
          };
        }
      | {
          name: 'button';
          props: {
            disabled: boolean;
          };
        } {
      if (this.to) return { name: 'RouterLink', props: { to: this.to, target: this.target } };

      if (this.href) return { name: 'a', props: { href: this.href, target: this.target } };

      if (this.toggle) {
        return {
          name: 'CheckBoxToggle',
          props: {
            position: 'end',
            checked: this.isSelected,
            id: this.computedId,
            disabled: this.isDisabled,
          },
        };
      }

      return {
        name: 'button',
        props: {
          disabled: this.isDisabled,
        },
      };
    },

    hasSlot() {
      return {
        'action-icon': hasSlotContent(this.$slots['action-icon'], {}),
        'info-icon': hasSlotContent(this.$slots['info-icon'], {}),
      };
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';
@use 'mixins/typography';
.popover-menu__item {
  border-radius: settings.$border-radius;
  cursor: pointer;
  width: 100%;

  &:hover,
  &.highlighted {
    background-color: colors.$color-grey-100;
  }

  &.destructive {
    .primary-text,
    .info-icon {
      color: colors.$color-red-600;
    }
    &:hover {
      background-color: colors.$color-red-50;
    }
  }

  &--disabled {
    &:hover {
      background-color: inherit;
    }

    .primary-text,
    .info-icon {
      color: colors.$color-grey-400;
    }

    button,
    input {
      cursor: not-allowed;
    }
  }
}

.primary-text {
  display: inline-block;
  align-items: center;
  color: colors.$color-grey-900;
  @include typography.typography-ellipsis;
}

.info-icon {
  flex: 0 0 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: colors.$color-grey-600;
}

.popover-menu__item-component {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 4px;
  padding: 0 4px 0 8px;
  height: 32px;
  border-radius: settings.$border-radius;
  line-height: 1.5;

  &.checkbox-toggle {
    &:focus-within {
      outline: settings.$popover-menu-focus;
    }
  }
}

a.popover-menu__item-component,
button.popover-menu__item-component {
  &:focus-visible,
  &:focus {
    outline: settings.$popover-menu-focus;
  }
}

.popover-menu__item-action-icon {
  flex: 0 0 min-content;
  display: flex;
  gap: 4px;
  align-items: center;
  margin-left: auto;
  padding-left: 4px;
}

.popover-menu__item-shortcut {
  margin-left: auto;
}
</style>
